import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api";
import {
  callErrorMessage,
  summarizeMultipleCalls,
  type MultipleCallsSummary,
} from "@/lib/apcmCall";
import { PatientResponse } from "@/lib/types";
import { useOrganizationsStore } from "@/stores/organizationsStore";

interface APCMCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patients: PatientResponse[] | PatientResponse;
  verifiedPhoneNumberId: string;
  onSuccess?: () => void;
}

const APCMCallDialog: React.FC<APCMCallDialogProps> = ({
  open,
  onOpenChange,
  patients,
  verifiedPhoneNumberId,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [isPlacingCall, setIsPlacingCall] = useState(false);
  // Shown in the dialog when some patients did not get a call (refused by policy, no phone, ...).
  const [summary, setSummary] = useState<MultipleCallsSummary | null>(null);
  const { selectedOrganizationId } = useOrganizationsStore();

  const handleOpenChange = (next: boolean) => {
    if (!next) setSummary(null);
    onOpenChange(next);
  };

  // Determine if this is a single patient or multiple patients
  const isMultiplePatients = Array.isArray(patients);
  const patientList = isMultiplePatients ? patients : [patients];

  const handleAPCMCall = async () => {
    if (!selectedOrganizationId || !verifiedPhoneNumberId) {
      toast({
        title: "Error",
        description: "Missing organization ID or phone number ID",
        variant: "destructive",
      });
      return;
    }

    try {
      const organization = await api.getOrganization(selectedOrganizationId);

      if (!organization.default_apcm_strategy_version_id) {
        toast({
          title: "Error",
          description:
            "No default APCM strategy version set for this organization. Please set one in Organization Settings.",
          variant: "destructive",
        });
        return;
      }

      setIsPlacingCall(true);

      if (isMultiplePatients) {
        const patientIds = patientList.map((patient) => patient.id);
        const result = await api.makeMultipleAPCMCalls(
          patientIds,
          selectedOrganizationId,
          organization.default_apcm_strategy_version_id,
          verifiedPhoneNumberId
        );
        const s = summarizeMultipleCalls(result, patientList);
        toast({
          title: s.problems.length ? "Some calls not placed" : "Calls placed",
          description: s.headline,
          variant: s.started === 0 && s.problems.length ? "destructive" : "default",
        });
        if (s.started > 0) onSuccess?.();
        if (s.problems.length) {
          // Keep the dialog open so staff can see who was not called and why.
          setSummary(s);
          return;
        }
      } else {
        const patient = patientList[0];
        await api.makeAPCMCall(
          patient.id,
          selectedOrganizationId,
          organization.default_apcm_strategy_version_id,
          verifiedPhoneNumberId
        );
        toast({
          title: "Call placed",
          description: `Calling ${patient.first_name} ${patient.last_name} at ${patient.phone_number}.`,
          variant: "default",
        });
        onSuccess?.();
      }

      handleOpenChange(false);
    } catch (error) {
      console.error("Error making APCM call:", error);
      toast({
        title: "Call not placed",
        description: callErrorMessage(error),
        variant: "destructive",
      });
    } finally {
      setIsPlacingCall(false);
    }
  };

  // Get a comma-separated list of patient names or count for the dialog
  const getPatientDescription = () => {
    if (isMultiplePatients) {
      if (patientList.length > 3) {
        return `${patientList.length} selected patients`;
      } else {
        return patientList
          .map((p) => `${p.first_name} ${p.last_name}`)
          .join(", ");
      }
    } else {
      const patient = patientList[0];
      return `${patient.first_name} ${patient.last_name} (${patient.phone_number})`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Confirm APCM {isMultiplePatients ? "Calls" : "Call"}
          </DialogTitle>
          <DialogDescription>
            {summary
              ? summary.headline
              : isMultiplePatients
                ? `Calls will be placed now to ${getPatientDescription()}. Would you like to proceed?`
                : `A call will be placed now to ${getPatientDescription()}. Would you like to proceed?`}
          </DialogDescription>
        </DialogHeader>
        {summary ? (
          <div className="space-y-2">
            <p className="text-sm font-medium">Not called:</p>
            <ul className="max-h-64 space-y-1.5 overflow-y-auto rounded-md border p-3 text-sm">
              {summary.problems.map((p) => (
                <li key={p.patientId} className="break-words">
                  <span className="font-medium">{p.name}</span>
                  <span className="text-muted-foreground">: {p.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            Calls follow the calling rules: weekdays 8am-7pm Central, and never to a patient who
            refused, already consented, or is paused. A call the rules refuse is not placed, and the
            reason is shown.
          </p>
        )}
        <DialogFooter>
          {summary ? (
            <Button onClick={() => handleOpenChange(false)}>Close</Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isPlacingCall}
              >
                Cancel
              </Button>
              <Button onClick={handleAPCMCall} disabled={isPlacingCall}>
                {isPlacingCall ? "Placing Call..." : "Confirm"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default APCMCallDialog;
